import { useEffect, useState } from 'react';
import { Icons } from '../Icons.jsx';
import { speak } from '../../utils/speech.js';
import { tapLight } from '../../utils/haptics.js';
import { BALL } from '../../theme.js';
import { t } from '../../i18n.js';
import { Sentry } from '../../sentry.js';

const T = BALL;

function ImageContent({ src, size }) {
  // Emoji values are short strings without a leading slash; image paths
  // start with `/`. Renderer picks the right node per source type.
  const isUrl = typeof src === 'string' && src.startsWith('/');
  if (isUrl) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }
  return (
    <span aria-hidden="true" style={{ fontSize: size * 0.7, lineHeight: 1 }}>
      {src}
    </span>
  );
}

export default function ImageChoice({ exercise, lang, onResult, locked }) {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
    const tid = setTimeout(() => speak(exercise.speakText), 250);
    return () => clearTimeout(tid);
  }, [exercise]);

  function pick(opt) {
    if (locked || selected) return;
    tapLight();
    setSelected(opt.hr);
    const correct = opt.hr === exercise.correct;
    Sentry.addBreadcrumb({ category: 'exercise', message: 'image-choice-submit', data: { correct } });
    onResult({ correct, given: opt.hr, expected: exercise.correct });
  }

  const eyebrow = lang === 'en' ? 'PICK THE RIGHT IMAGE' : 'VÄLJ RÄTT BILD';
  const title = lang === 'en' ? 'Tap the matching image' : 'Tryck på rätt bild';

  return (
    <div style={{ padding: '4px 22px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{
          fontSize: 11, fontWeight: 800, color: T.blue, letterSpacing: '0.12em',
        }}>{eyebrow}</div>
        <h2 style={{
          fontSize: 24, fontWeight: 900, color: T.ink, margin: '6px 0 0', lineHeight: 1.15,
        }}>{title}</h2>
      </div>

      <button
        type="button"
        onClick={() => speak(exercise.speakText)}
        style={{
          appearance: 'none', cursor: 'pointer',
          background: T.bgAlt, border: `2px solid ${T.hairline}`,
          borderRadius: 16, padding: '14px 16px',
          boxShadow: `0 3px 0 ${T.hairline}`,
          display: 'flex', alignItems: 'center', gap: 14,
          textAlign: 'left', fontFamily: 'inherit', width: '100%',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: T.blue, color: '#fff', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 3px 0 ${T.blueDeep}`,
        }}>
          {Icons.speaker('#fff')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.primary }}>{exercise.prompt}</div>
          <div style={{
            fontSize: 11, fontWeight: 800, color: T.primary,
            letterSpacing: '0.06em', marginTop: 4, opacity: 0.85,
          }}>
            ▶ {t('tapToHear', lang).toUpperCase()}
          </div>
        </div>
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {exercise.options.map((opt) => {
          const isSel = selected === opt.hr;
          const isCorrect = isSel && opt.hr === exercise.correct;
          const isWrong = isSel && opt.hr !== exercise.correct;
          let borderColor = T.hairline;
          let shadowColor = T.hairline;
          if (isCorrect) { borderColor = T.mint; shadowColor = T.mintDeep; }
          else if (isWrong) { borderColor = T.red; shadowColor = T.redDeep; }
          else if (isSel) { borderColor = T.blue; shadowColor = T.blueDeep; }

          return (
            <button
              key={opt.hr}
              type="button"
              onClick={() => pick(opt)}
              disabled={locked || !!selected}
              style={{
                appearance: 'none',
                cursor: locked || selected ? 'not-allowed' : 'pointer',
                background: T.bgAlt,
                border: `3px solid ${borderColor}`,
                boxShadow: `0 4px 0 ${shadowColor}`,
                borderRadius: 14,
                padding: 0,
                aspectRatio: '1 / 1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'inherit',
                transition: 'border-color 120ms, box-shadow 120ms',
              }}
            >
              <ImageContent src={opt.src} size={120} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
